/**
 * Canvas plots.
 *
 * Canvas rather than SVG because these redraw on every hardware change and some
 * of them (spectrogram, vectorscope) redraw every frame. All of them are
 * device-pixel-ratio aware so hairlines stay hairlines on a HiDPI display.
 */

import { useEffect, useRef } from 'react';
import type { FRCurve, ImpedanceCurve } from '../../types/curves';
import { evalFR, evalImpedance } from '../../types/curves';

const GRID = '#171d25';
const GRID_MAJOR = '#232b36';
const AXIS_TEXT = '#5a6470';

export interface PlotSeries {
  /** Either a curve or an explicit sampled response. */
  curve?: FRCurve;
  freq?: Float32Array;
  values?: Float32Array;
  color: string;
  label?: string;
  width?: number;
  dashed?: boolean;
  fill?: boolean;
}

function setupCanvas(canvas: HTMLCanvasElement): { ctx: CanvasRenderingContext2D; w: number; h: number } | null {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const rect = canvas.getBoundingClientRect();
  const w = Math.max(1, Math.floor(rect.width));
  const h = Math.max(1, Math.floor(rect.height));
  if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
    canvas.width = w * dpr;
    canvas.height = h * dpr;
  }
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);
  return { ctx, w, h };
}

const FREQ_TICKS = [10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000, 40000];
const FREQ_LABELS: Record<number, string> = {
  10: '10', 20: '20', 50: '50', 100: '100', 200: '200', 500: '500',
  1000: '1k', 2000: '2k', 5000: '5k', 10000: '10k', 20000: '20k', 40000: '40k',
};

export interface FrequencyPlotProps {
  series: PlotSeries[];
  fMin?: number;
  fMax?: number;
  dbMin?: number;
  dbMax?: number;
  height?: number;
  yLabel?: string;
  /** Draw a horizontal reference line at this value. */
  reference?: number;
  className?: string;
}

export function FrequencyPlot({
  series,
  fMin = 10,
  fMax = 30000,
  dbMin = -24,
  dbMax = 12,
  height = 180,
  yLabel = 'dB',
  reference = 0,
  className,
}: FrequencyPlotProps) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const draw = () => {
      const s = setupCanvas(canvas);
      if (!s) return;
      const { ctx, w, h } = s;
      const padL = 34;
      const padR = 8;
      const padT = 8;
      const padB = 18;
      const pw = Math.max(1, w - padL - padR);
      const ph = Math.max(1, h - padT - padB);

      const xOf = (f: number) => padL + (Math.log(Math.max(f, fMin)) - Math.log(fMin)) / (Math.log(fMax) - Math.log(fMin)) * pw;
      const yOf = (db: number) => padT + ph - ((db - dbMin) / (dbMax - dbMin)) * ph;

      ctx.font = '9px ui-monospace, monospace';
      ctx.textBaseline = 'middle';

      // dB grid
      const range = dbMax - dbMin;
      const stepDb = range > 90 ? 20 : range > 45 ? 10 : range > 18 ? 5 : range > 8 ? 2 : 1;
      for (let db = Math.ceil(dbMin / stepDb) * stepDb; db <= dbMax; db += stepDb) {
        const y = yOf(db);
        ctx.strokeStyle = Math.abs(db - reference) < 1e-9 ? GRID_MAJOR : GRID;
        ctx.beginPath();
        ctx.moveTo(padL, Math.round(y) + 0.5);
        ctx.lineTo(w - padR, Math.round(y) + 0.5);
        ctx.stroke();
        ctx.fillStyle = AXIS_TEXT;
        ctx.textAlign = 'right';
        ctx.fillText(String(db), padL - 4, y);
      }

      // Frequency grid
      ctx.textAlign = 'center';
      for (const f of FREQ_TICKS) {
        if (f < fMin || f > fMax) continue;
        const x = Math.round(xOf(f)) + 0.5;
        ctx.strokeStyle = f === 1000 ? GRID_MAJOR : GRID;
        ctx.beginPath();
        ctx.moveTo(x, padT);
        ctx.lineTo(x, padT + ph);
        ctx.stroke();
        ctx.fillStyle = AXIS_TEXT;
        ctx.fillText(FREQ_LABELS[f] ?? String(f), x, h - padB / 2);
      }

      // Series
      for (const ser of series) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(padL, padT, pw, ph);
        ctx.clip();
        ctx.strokeStyle = ser.color;
        ctx.lineWidth = ser.width ?? 1.5;
        ctx.lineJoin = 'round';
        if (ser.dashed) ctx.setLineDash([3, 3]);
        ctx.beginPath();
        let started = false;
        const N = 400;
        for (let i = 0; i < N; i++) {
          const f = fMin * Math.pow(fMax / fMin, i / (N - 1));
          let v: number;
          if (ser.curve) {
            v = evalFR(ser.curve, f);
          } else if (ser.freq && ser.values) {
            v = sampleAt(ser.freq, ser.values, f);
          } else {
            continue;
          }
          if (!Number.isFinite(v)) continue;
          const x = xOf(f);
          const y = yOf(v);
          if (!started) {
            ctx.moveTo(x, y);
            started = true;
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.stroke();
        if (ser.fill && started) {
          ctx.lineTo(xOf(fMax), yOf(dbMin));
          ctx.lineTo(xOf(fMin), yOf(dbMin));
          ctx.closePath();
          ctx.globalAlpha = 0.12;
          ctx.fillStyle = ser.color;
          ctx.fill();
        }
        ctx.restore();
      }

      // Frame
      ctx.strokeStyle = GRID_MAJOR;
      ctx.lineWidth = 1;
      ctx.strokeRect(padL + 0.5, padT + 0.5, pw - 1, ph - 1);

      // Y label
      ctx.save();
      ctx.translate(9, padT + ph / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.textAlign = 'center';
      ctx.fillStyle = AXIS_TEXT;
      ctx.fillText(yLabel, 0, 0);
      ctx.restore();
    };
    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [series, fMin, fMax, dbMin, dbMax, yLabel, reference]);

  return <canvas ref={ref} className={className} style={{ width: '100%', height, display: 'block' }} />;
}

function sampleAt(freq: Float32Array, values: Float32Array, f: number): number {
  if (freq.length === 0) return 0;
  if (f <= freq[0]) return values[0];
  if (f >= freq[freq.length - 1]) return values[values.length - 1];
  let lo = 0;
  let hi = freq.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (freq[mid] <= f) lo = mid;
    else hi = mid;
  }
  const t = (f - freq[lo]) / (freq[hi] - freq[lo] || 1);
  return values[lo] + t * (values[hi] - values[lo]);
}

// ---------------------------------------------------------------------------
// Impedance
// ---------------------------------------------------------------------------

export function ImpedancePlot({ curve, height = 150 }: { curve: ImpedanceCurve; height?: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const draw = () => {
      const s = setupCanvas(canvas);
      if (!s) return;
      const { ctx, w, h } = s;
      const padL = 34;
      const padR = 30;
      const padT = 8;
      const padB = 18;
      const pw = w - padL - padR;
      const ph = h - padT - padB;

      let zMax = 0;
      for (const [, m] of curve.points) zMax = Math.max(zMax, m);
      const top = Math.max(8, Math.pow(10, Math.ceil(Math.log10(Math.max(1, zMax)))));
      const fMin = 10;
      const fMax = 30000;

      const xOf = (f: number) => padL + ((Math.log(f) - Math.log(fMin)) / (Math.log(fMax) - Math.log(fMin))) * pw;
      const yOf = (z: number) => padT + ph - (Math.log10(Math.max(0.5, z)) / Math.log10(top)) * ph;
      const yPhase = (p: number) => padT + ph / 2 - (p / 90) * (ph / 2);

      ctx.font = '9px ui-monospace, monospace';
      ctx.textBaseline = 'middle';

      for (const z of [1, 2, 4, 8, 16, 32, 64, 128, 256, 1024, 4096, 16384, 65536]) {
        if (z > top) break;
        const y = yOf(z);
        ctx.strokeStyle = z === 8 ? GRID_MAJOR : GRID;
        ctx.beginPath();
        ctx.moveTo(padL, Math.round(y) + 0.5);
        ctx.lineTo(w - padR, Math.round(y) + 0.5);
        ctx.stroke();
        ctx.fillStyle = AXIS_TEXT;
        ctx.textAlign = 'right';
        ctx.fillText(z >= 1000 ? `${z / 1000}k` : String(z), padL - 4, y);
      }
      ctx.textAlign = 'center';
      for (const f of FREQ_TICKS) {
        if (f < fMin || f > fMax) continue;
        const x = Math.round(xOf(f)) + 0.5;
        ctx.strokeStyle = GRID;
        ctx.beginPath();
        ctx.moveTo(x, padT);
        ctx.lineTo(x, padT + ph);
        ctx.stroke();
        ctx.fillStyle = AXIS_TEXT;
        ctx.fillText(FREQ_LABELS[f] ?? String(f), x, h - padB / 2);
      }

      // Phase (right axis)
      ctx.strokeStyle = '#b48ead';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      for (let i = 0; i < 300; i++) {
        const f = fMin * Math.pow(fMax / fMin, i / 299);
        const { phase } = evalImpedance(curve, f);
        const x = xOf(f);
        const y = yPhase(phase);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
      ctx.setLineDash([]);

      // Magnitude
      ctx.strokeStyle = '#45c8b8';
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      for (let i = 0; i < 300; i++) {
        const f = fMin * Math.pow(fMax / fMin, i / 299);
        const { mag } = evalImpedance(curve, f);
        const x = xOf(f);
        const y = yOf(mag);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();

      ctx.fillStyle = '#b48ead';
      ctx.textAlign = 'left';
      ctx.fillText('+90', w - padR + 3, yPhase(90));
      ctx.fillText('0', w - padR + 3, yPhase(0));
      ctx.fillText('-90', w - padR + 3, yPhase(-90));

      ctx.strokeStyle = GRID_MAJOR;
      ctx.strokeRect(padL + 0.5, padT + 0.5, pw - 1, ph - 1);
    };
    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [curve]);
  return <canvas ref={ref} style={{ width: '100%', height, display: 'block' }} />;
}

// ---------------------------------------------------------------------------
// Impulse / step response
// ---------------------------------------------------------------------------

export function ImpulsePlot({
  taps,
  height = 140,
  color = '#6ea8ff',
  showStep,
  sampleRate,
}: {
  taps: Float32Array | null;
  height?: number;
  color?: string;
  showStep?: boolean;
  sampleRate?: number;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const draw = () => {
      const s = setupCanvas(canvas);
      if (!s) return;
      const { ctx, w, h } = s;
      if (!taps || taps.length === 0) {
        ctx.fillStyle = AXIS_TEXT;
        ctx.font = '11px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('No impulse response for this stage', w / 2, h / 2);
        return;
      }
      const padL = 6;
      const padR = 6;
      const padT = 6;
      const padB = 14;
      const pw = w - padL - padR;
      const ph = h - padT - padB;

      let peak = 0;
      for (let i = 0; i < taps.length; i++) peak = Math.max(peak, Math.abs(taps[i]));
      const step = new Float32Array(taps.length);
      if (showStep) {
        let acc = 0;
        for (let i = 0; i < taps.length; i++) {
          acc += taps[i];
          step[i] = acc;
        }
      }
      let stepPeak = 0;
      for (let i = 0; i < step.length; i++) stepPeak = Math.max(stepPeak, Math.abs(step[i]));
      const norm = showStep ? Math.max(peak, stepPeak) : peak;
      if (norm <= 0) return;

      const yOf = (v: number) => padT + ph / 2 - (v / norm) * (ph / 2) * 0.92;
      const xOf = (i: number) => padL + (i / Math.max(1, taps.length - 1)) * pw;

      ctx.strokeStyle = GRID_MAJOR;
      ctx.beginPath();
      ctx.moveTo(padL, Math.round(yOf(0)) + 0.5);
      ctx.lineTo(w - padR, Math.round(yOf(0)) + 0.5);
      ctx.stroke();

      if (showStep) {
        ctx.strokeStyle = '#ffc857';
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        for (let i = 0; i < step.length; i++) {
          const x = xOf(i);
          const y = yOf(step[i]);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }

      ctx.strokeStyle = color;
      ctx.lineWidth = 1.3;
      ctx.beginPath();
      for (let i = 0; i < taps.length; i++) {
        const x = xOf(i);
        const y = yOf(taps[i]);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();

      ctx.fillStyle = AXIS_TEXT;
      ctx.font = '9px ui-monospace, monospace';
      ctx.textAlign = 'left';
      ctx.fillText(`${taps.length} taps`, padL + 2, h - 5);
      if (sampleRate) {
        ctx.textAlign = 'right';
        ctx.fillText(`${((taps.length / sampleRate) * 1000).toFixed(2)} ms`, w - padR - 2, h - 5);
      }
    };
    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [taps, color, showStep, sampleRate]);
  return <canvas ref={ref} style={{ width: '100%', height, display: 'block' }} />;
}

// ---------------------------------------------------------------------------
// Transfer function / harmonic spectrum
// ---------------------------------------------------------------------------

export function TransferPlot({
  fn,
  height = 150,
  color = '#45c8b8',
}: {
  fn: (x: number) => number;
  height?: number;
  color?: string;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const draw = () => {
      const s = setupCanvas(canvas);
      if (!s) return;
      const { ctx, w, h } = s;
      const pad = 8;
      const size = Math.min(w, h) - pad * 2;
      const ox = (w - size) / 2;
      const oy = (h - size) / 2;

      ctx.strokeStyle = GRID;
      for (let i = 0; i <= 4; i++) {
        const t = i / 4;
        ctx.beginPath();
        ctx.moveTo(ox, Math.round(oy + t * size) + 0.5);
        ctx.lineTo(ox + size, Math.round(oy + t * size) + 0.5);
        ctx.moveTo(Math.round(ox + t * size) + 0.5, oy);
        ctx.lineTo(Math.round(ox + t * size) + 0.5, oy + size);
        ctx.stroke();
      }
      // Unity reference
      ctx.strokeStyle = GRID_MAJOR;
      ctx.setLineDash([2, 3]);
      ctx.beginPath();
      ctx.moveTo(ox, oy + size);
      ctx.lineTo(ox + size, oy);
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.strokeStyle = color;
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      for (let i = 0; i <= 240; i++) {
        const x = -1 + (2 * i) / 240;
        const y = Math.max(-1.4, Math.min(1.4, fn(x)));
        const px = ox + ((x + 1) / 2) * size;
        const py = oy + size - ((y + 1.4) / 2.8) * size;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.stroke();

      ctx.strokeStyle = GRID_MAJOR;
      ctx.strokeRect(ox + 0.5, oy + 0.5, size - 1, size - 1);
      ctx.fillStyle = AXIS_TEXT;
      ctx.font = '9px ui-monospace, monospace';
      ctx.textAlign = 'center';
      ctx.fillText('input', ox + size / 2, oy + size + 10);
    };
    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [fn, color]);
  return <canvas ref={ref} style={{ width: '100%', height, display: 'block' }} />;
}

export function HarmonicBars({
  harmonics,
  height = 130,
}: {
  harmonics: Array<{ order: number; db: number }>;
  height?: number;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const draw = () => {
      const s = setupCanvas(canvas);
      if (!s) return;
      const { ctx, w, h } = s;
      const padL = 30;
      const padB = 16;
      const padT = 6;
      const pw = w - padL - 6;
      const ph = h - padB - padT;
      const floor = -160;
      const top = 0;

      ctx.font = '9px ui-monospace, monospace';
      ctx.textBaseline = 'middle';
      for (let db = 0; db >= floor; db -= 40) {
        const y = padT + ph - ((db - floor) / (top - floor)) * ph;
        ctx.strokeStyle = GRID;
        ctx.beginPath();
        ctx.moveTo(padL, Math.round(y) + 0.5);
        ctx.lineTo(w - 6, Math.round(y) + 0.5);
        ctx.stroke();
        ctx.fillStyle = AXIS_TEXT;
        ctx.textAlign = 'right';
        ctx.fillText(String(db), padL - 4, y);
      }

      const n = harmonics.length || 1;
      const bw = Math.max(3, (pw / n) * 0.55);
      harmonics.forEach((hm, i) => {
        const x = padL + (pw * (i + 0.5)) / n;
        const v = Math.max(floor, Math.min(top, hm.db));
        const y = padT + ph - ((v - floor) / (top - floor)) * ph;
        ctx.fillStyle = hm.order % 2 === 0 ? '#45c8b8' : '#ff9f6e';
        ctx.fillRect(x - bw / 2, y, bw, padT + ph - y);
        ctx.fillStyle = AXIS_TEXT;
        ctx.textAlign = 'center';
        ctx.fillText(`H${hm.order}`, x, h - padB / 2);
      });

      ctx.strokeStyle = GRID_MAJOR;
      ctx.strokeRect(padL + 0.5, padT + 0.5, pw - 1, ph - 1);
    };
    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [harmonics]);
  return <canvas ref={ref} style={{ width: '100%', height, display: 'block' }} />;
}
